import {
  PackageSearchCategory,
  TravelMethodId,
  TravelTypeId,
} from 'leash/utilities/const';
import { Airport, Nullable, TransporterCompany, TravelProposal } from 'types';
import { Media } from 'pricing/types';

export type TravelMethodIdType =
  | typeof TravelMethodId.Meetup
  | typeof TravelMethodId.GroundTransportation
  | typeof TravelMethodId.CommercialCargo
  | typeof TravelMethodId.CharterFlight
  | typeof TravelMethodId.Chaperone
  | typeof TravelMethodId.LocalDelivery
  | typeof TravelMethodId.HubPassThru;

export type TravelTypeIdType =
  | typeof TravelTypeId.Meetup
  | typeof TravelTypeId.PickNearHome
  | typeof TravelTypeId.HomeDelivery
  | typeof TravelTypeId.ChaperoneAirportDelivery
  | typeof TravelTypeId.ChaperoneHomeDelivery;

export type PackageSearchCategories =
  | typeof PackageSearchCategory.PuppyId
  | typeof PackageSearchCategory.BreederId
  | typeof PackageSearchCategory.ContactId
  | typeof PackageSearchCategory.Breed
  | typeof PackageSearchCategory.BreederName
  | typeof PackageSearchCategory.Airport
  | typeof PackageSearchCategory.Airline
  | typeof PackageSearchCategory.Transporter
  | typeof PackageSearchCategory.GroundTransporter;

export type SearchTravelPlaningEntity = 'customer' | 'puppy';

export interface SearchTravelPlaningPayload {
  entity: SearchTravelPlaningEntity;
  entityId: number;
}

export interface ArchivedSearch {
  searchBy: SearchTravelPlaningEntity;
  searchValue: number;
}

export interface PackagesTableActiveFooterTools {
  exportCSV?: boolean;
}

export interface TravelPackagesHistory {
  id: number;
  changedAt: string;
  changedByUser: {
    firstName: string;
    lastName: string;
  };
  historyActionType: {
    name: string;
    slug: string;
  };
  historyField: {
    label: string;
    modelProperty: HistoryFieldModelProperty;
  };
  newValue: TravelPackagesHistoryValue;
  oldValue: TravelPackagesHistoryValue;
}

export interface TravelPackagesHistoryTableData {
  key?: number;
  dateTime: string;
  actionType: string;
  user: string;
  field: string;
  oldValue: TravelPackagesHistoryValue;
  newValue: TravelPackagesHistoryValue;
}

export type TravelPackagesHistoryValue = Nullable<string | number>;

export type HistoryFieldModelProperty =
  | 'occurred_at'
  | 'travel_type_id'
  | 'arrival_time'
  | 'departure_time'
  | 'created_at'
  | 'updated_at'
  | 'deleted_at';

export type ExportCsvDataType = 'travelPackages' | 'travelPackagesHistory';

export interface TravelType {
  isActive: boolean;
  id: number;
  label: string;
  name: string;
}

export interface TravelMethod {
  id: TravelMethodIdType;
  name: string;
  isAllowedForRestrictedBreeds: boolean;
  isEligible?: boolean;
}

export interface TravelEligibility {
  ineligibleReasons: string[];
  isEligible: boolean;
  travelMethod: TravelMethod;
  travelType: TravelType;
  originHubAirport?: Airport;
  destinationHubAirport?: Airport;
  pickupLocation?: TransporterCompany;
  airport?: Airport;
}

export interface UpdateTravelMethodParameters {
  orderId: number;
  travelMethodId: TravelMethodIdType;
}

export interface TravelPackage {
  id: number;
  workflowStatus: Nullable<string>;
  travelTypeId: TravelTypeIdType;
  travelType: Nullable<string>;
  travelMethodId: TravelMethodIdType;
  travelMethod: Nullable<string>;
  travelProposal: Nullable<TravelProposal>;
  blackoutDates?: string[];
}

export enum HealthDocumentTypeId {
  HealthCertificate = 1,
  VaccineRecord,
  VeterinaryHealthReport,
}

export enum HealthDocumentWorkflowStatusId {
  PendingReview = 23,
  HealthReview,
  Approved,
  Rejected,
  AwaitingDisclosure,
  NotUploaded = 0,
}

export interface HealthDocument {
  id: number;
  documentTypeId: HealthDocumentTypeId;
  expiresAt: string | null;
  updatedAt: string;
  createdAt: string;
  media: Media[];
  workflowEntity: Nullable<{
    currentWorkflowStatusId: HealthDocumentWorkflowStatusId;
  }>;
}

export const HealthDocumentTypeLabels: Record<
  keyof typeof HealthDocumentTypeId | string,
  string
> = {
  HealthCertificate: 'HC',
  VaccineRecord: 'VAX',
  VeterinaryHealthReport: 'VHR',
} as const;

export const HealthDocumentWorkflowStatusLabel: Record<
  keyof typeof HealthDocumentWorkflowStatusId | string,
  string
> = {
  NotUploaded: 'Not Uploaded',
  PendingReview: 'Pending Review',
  HealthReview: 'Pending Review',
  Approved: 'Approved',
  Rejected: 'Rejected',
  AwaitingDisclosure: 'Awaiting Disclosure',
} as const;
